'use client';

import { useState, useRef, useEffect } from 'react';
import {
  User,
  Mail,
  Shield,
  Calendar,
  Camera,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Globe,
  ChevronDown,
  Check,
  Lock,
  Copy,
  Smartphone,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';
import Avatar from '@/components/Avatar';
import { useTranslation } from '@/providers/I18nProvider';

interface UserProfileProps {
  agentId: string;
  onClose: () => void;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    authType?: 'PASSWORD' | 'SAML';
    avatarUrl?: string;
    createdAt?: string;
    languagePreference?: string;
    totpEnabled?: boolean;
  };
  onUserUpdate?: () => void;
  allowPersonalLanguagePreference?: boolean;
  chatMultilingualEnabled?: boolean;
  twoFactorConfig?: {
    required: boolean;
    rememberDeviceDays: number;
  };
}

const availableLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'jp', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
];

export default function UserProfile({ agentId, onClose, user, onUserUpdate, allowPersonalLanguagePreference = false, chatMultilingualEnabled = false, twoFactorConfig }: UserProfileProps) {
  const { t } = useTranslation();
  
  // Check if this is the special admin user (configured in agent's configDoc)
  const isSpecialAdmin = user.id === 'admin';
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    languagePreference: user?.languagePreference || 'en',
  });
  
  // 2FA state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [setting2FA, setSetting2FA] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

  // Handle click outside for language menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLanguageMenu]);

  if (!user) {
    return null;
  }

  // Handle profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Updating profile with data:', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        languagePreference: formData.languagePreference,
        userId: user.id,
        agentId: agentId
      });

      const response = await fetch(`/api/chat/agents/${agentId}/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          ...(chatMultilingualEnabled && allowPersonalLanguagePreference && !isSpecialAdmin && { languagePreference: formData.languagePreference }),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Profile update failed:', errorData);
        throw new Error(errorData.error || 'Failed to update profile');
      }

      setSuccess('Profile updated successfully');
      
      // If language preference was changed, reload immediately to apply the new language
      if (formData.languagePreference !== user.languagePreference) {
        setTimeout(() => {
          if (onUserUpdate) {
            onUserUpdate();
          }
        }, 500); // Small delay to show success message
      } else {
        // For other updates, just notify parent
        if (onUserUpdate) {
          onUserUpdate();
        }
      }
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (file: File) => {
    if (!file) return;

    setUploadingAvatar(true);
    setError('');
    
    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await fetch(`/api/chat/agents/${agentId}/users/${user.id}/avatar`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload avatar');
      }

      setSuccess('Avatar uploaded successfully');
      // Notify parent to refresh user data
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Handle avatar file selection
  const handleAvatarFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAvatarUpload(file);
    }
  };

  // Handle copy user ID
  const handleCopyUserId = async () => {
    try {
      await navigator.clipboard.writeText(user.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch (err) {
      console.error('Failed to copy user ID:', err);
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    setSetting2FA(true);
    setError('');
    
    try {
      const response = await fetch(`/api/chat/agents/${agentId}/users/2fa/setup`, {
        method: 'POST',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to setup 2FA');
      }

      const data = await response.json();
      setQrCode(data.qrCode);
      setTotpSecret(data.secret);
      setBackupCodes(data.backupCodes);
      setShow2FASetup(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to setup 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!totpToken || totpToken.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setVerifying2FA(true);
    setError('');

    try {
      const response = await fetch(`/api/chat/agents/${agentId}/users/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: totpToken }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Invalid verification code');
      }

      setSuccess('2FA has been successfully enabled');
      setShow2FASetup(false);
      setQrCode('');
      setTotpSecret('');
      setBackupCodes('');
      setTotpToken('');
      
      // Refresh user data
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify 2FA');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      setError('Please enter your password');
      return;
    }

    setDisabling2FA(true);
    setError('');

    try {
      const response = await fetch(`/api/chat/agents/${agentId}/users/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: disablePassword }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to disable 2FA');
      }

      setSuccess('2FA has been disabled');
      setShowDisableDialog(false);
      setDisablePassword('');
      
      // Refresh user data
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(totpSecret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch (err) {
      console.error('Failed to copy secret:', err);
    }
  };

  const handleCopyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes);
      setCopiedBackupCodes(true);
      setTimeout(() => setCopiedBackupCodes(false), 2000);
    } catch (err) {
      console.error('Failed to copy backup codes:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-[10000]">
          <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 z-50">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('profile.title')}</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Success/Error Messages */}
            {error && (
              <div className="mb-6 flex items-start gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
                <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {success && (
              <div className="mb-6 flex items-start gap-2 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-green-700 dark:text-green-300">{success}</p>
                </div>
                <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Profile Picture Section - Hide for special admin user */}
            {!isSpecialAdmin && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('profile.sections.profilePicture')}</h3>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <Avatar
                      src={user.avatarUrl}
                      firstName={user.firstName}
                      lastName={user.lastName}
                      size="xl"
                    />
                    <label className="absolute bottom-0 right-0 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full cursor-pointer transition-colors">
                      <Camera className="h-5 w-5" />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleAvatarFileSelect}
                        className="hidden"
                        disabled={uploadingAvatar}
                      />
                    </label>
                  </div>
                  <div>
                    <>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {uploadingAvatar ? t('profile.uploading') : t('profile.uploadInstruction')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('profile.uploadLimits')}
                      </p>
                      {user.authType === 'SAML' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('profile.samlAvatarNote')}
                        </p>
                      )}
                    </>
                  </div>
                </div>
              </div>
            )}

            {/* Personal Information Form */}
            <form onSubmit={handleUpdateProfile}>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('profile.sections.personalInfo')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.fields.email')}
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900 dark:text-gray-100">{user.email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.fields.userId')}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-gray-100 font-mono text-sm">{user.id}</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyUserId}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
                      title="Copy User ID"
                    >
                      {copiedId ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.fields.firstName')}
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      disabled={user.authType === 'SAML'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.fields.lastName')}
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      disabled={user.authType === 'SAML'}
                    />
                  </div>
                </div>

                {user.authType === 'SAML' && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {t('profile.samlNameNote')}
                    </p>
                  </div>
                )}

                {/* Language Preference - Show based on feature flags and disable for special admin */}
                {((chatMultilingualEnabled && allowPersonalLanguagePreference) || !chatMultilingualEnabled) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Globe className="inline h-4 w-4 mr-1" />
                      {t('profile.fields.languagePreference')}
                      {isSpecialAdmin && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400 rounded">
                          Disabled for Admin User
                        </span>
                      )}
                      {!isSpecialAdmin && !chatMultilingualEnabled && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                          {t('common.status.disabled')}
                        </span>
                      )}
                    </label>
                    <div className="relative" ref={languageMenuRef}>
                      <button
                        type="button"
                        onClick={() => !isSpecialAdmin && chatMultilingualEnabled && setShowLanguageMenu(!showLanguageMenu)}
                        disabled={isSpecialAdmin || !chatMultilingualEnabled}
                        className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                 flex items-center justify-between transition-colors
                                 ${!isSpecialAdmin && chatMultilingualEnabled ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                      >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">
                          {availableLanguages.find(lang => lang.code === formData.languagePreference)?.flag}
                        </span>
                        <span className="text-sm font-medium">
                          {availableLanguages.find(lang => lang.code === formData.languagePreference)?.name}
                        </span>
                      </div>
                      {isSpecialAdmin || !chatMultilingualEnabled ? (
                        <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      ) : (
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`} />
                      )}
                    </button>

                    {/* Language Dropdown Menu */}
                    {showLanguageMenu && !isSpecialAdmin && (
                      <div className="absolute top-full left-0 right-0 mt-2 w-full bg-white dark:bg-gray-800 
                                    rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 
                                    py-2 z-40 max-h-80 overflow-y-auto">
                        {availableLanguages.map((lang) => {
                          const isSelected = lang.code === formData.languagePreference;
                          return (
                            <button
                              key={lang.code}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, languagePreference: lang.code });
                                setShowLanguageMenu(false);
                              }}
                              className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 
                                       transition-colors text-left ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                            >
                              <span className="text-2xl">{lang.flag}</span>
                              <span className={`flex-1 text-sm ${isSelected ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                {lang.name}
                              </span>
                              {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.fields.role')}
                    </label>
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg">
                      {user.role === 'admin' ? (
                        <>
                          <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-blue-600 dark:text-blue-400 font-medium">{t('admin.users.roles.admin')}</span>
                        </>
                      ) : (
                        <>
                          <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          <span className="text-gray-900 dark:text-gray-100">{t('admin.users.roles.user')}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.fields.authentication')}
                    </label>
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg">
                      <Shield className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-gray-100">
                        {user.authType === 'SAML' ? t('admin.users.authTypes.saml') : t('admin.users.authTypes.password')}
                      </span>
                    </div>
                  </div>
                </div>

                {user.createdAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.fields.memberSince')}
                    </label>
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-gray-100">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* 2FA Section - Only show for password auth users and when 2FA is enabled for the agent */}
              {user.authType === 'PASSWORD' && twoFactorConfig?.required && (
                <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {t('profile.sections.twoFactorAuth')}
                  </h3>
                  
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Smartphone className="h-5 w-5 text-gray-600 dark:text-gray-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {user.totpEnabled ? t('profile.twoFactor.enabled') : t('profile.twoFactor.disabled')}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {user.totpEnabled 
                              ? t('profile.twoFactor.enabledDescription')
                              : t('profile.twoFactor.disabledDescription')}
                          </p>
                        </div>
                      </div>
                      
                      {user.totpEnabled ? (
                        <button
                          type="button"
                          onClick={() => setShowDisableDialog(true)}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 
                                   dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        >
                          {t('profile.twoFactor.disable')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSetup2FA}
                          disabled={setting2FA}
                          className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 
                                   dark:text-blue-400 dark:hover:text-blue-300 transition-colors
                                   disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {setting2FA ? t('profile.twoFactor.setting') : t('profile.twoFactor.setup')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('profile.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t('profile.saveButton')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* 2FA Setup Dialog */}
      {show2FASetup && (
        <div className="fixed inset-0 z-[10001] overflow-hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 z-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('profile.twoFactor.setupTitle')}
                  </h3>
                  <button
                    onClick={() => {
                      setShow2FASetup(false);
                      setQrCode('');
                      setTotpSecret('');
                      setBackupCodes('');
                      setTotpToken('');
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Step 1: Scan QR Code */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('profile.twoFactor.step1')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('profile.twoFactor.scanInstructions')}
                  </p>
                  {qrCode && (
                    <div className="flex justify-center p-4 bg-white rounded-lg">
                      <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                    </div>
                  )}
                </div>

                {/* Manual Entry Option */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('profile.twoFactor.cantScan')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t('profile.twoFactor.manualEntry')}
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={totpSecret}
                      readOnly
                      className="flex-1 px-3 py-2 font-mono text-xs bg-gray-50 dark:bg-gray-800 
                               border border-gray-300 dark:border-gray-700 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={handleCopySecret}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-300 
                               dark:border-gray-700 rounded-lg transition-colors"
                    >
                      {copiedSecret ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Step 2: Save Backup Codes */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('profile.twoFactor.step2')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t('profile.twoFactor.backupCodesWarning')}
                  </p>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowBackupCodes(!showBackupCodes)}
                      className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 
                               dark:text-blue-400 dark:hover:text-blue-300 border border-blue-600 
                               dark:border-blue-400 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {showBackupCodes ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      {showBackupCodes ? t('profile.twoFactor.hideBackupCodes') : t('profile.twoFactor.showBackupCodes')}
                    </button>
                    
                    {showBackupCodes && (
                      <div className="space-y-2">
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <pre className="font-mono text-xs text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                            {backupCodes}
                          </pre>
                        </div>
                        <button
                          type="button"
                          onClick={handleCopyBackupCodes}
                          className="w-full px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 
                                   dark:text-gray-300 dark:hover:text-gray-100 border border-gray-300 
                                   dark:border-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          {copiedBackupCodes ? (
                            <>
                              <Check className="h-4 w-4 text-green-500" />
                              {t('profile.twoFactor.copiedBackupCodes')}
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              {t('profile.twoFactor.copyBackupCodes')}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 3: Verify */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('profile.twoFactor.step3')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('profile.twoFactor.verifyInstructions')}
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={totpToken}
                      onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="flex-1 px-3 py-2 text-center font-mono text-lg border border-gray-300 
                               dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 
                               text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 
                               focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    <button
                      type="button"
                      onClick={handleVerify2FA}
                      disabled={verifying2FA || totpToken.length !== 6}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                               transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {verifying2FA ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('profile.twoFactor.verifying')}
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          {t('profile.twoFactor.verifyButton')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Disable Dialog */}
      {showDisableDialog && (
        <div className="fixed inset-0 z-[10001] overflow-hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('profile.twoFactor.disableTitle')}
                </h3>
              </div>
              
              <div className="p-6">
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 
                             dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {t('profile.twoFactor.disableWarning')}
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.twoFactor.enterPassword')}
                    </label>
                    <input
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      placeholder={t('profile.twoFactor.passwordPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDisableDialog(false);
                        setDisablePassword('');
                      }}
                      className="px-4 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-300 
                               dark:hover:text-gray-100 font-medium transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleDisable2FA}
                      disabled={disabling2FA || !disablePassword}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg
                               transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {disabling2FA ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('profile.twoFactor.disabling')}
                        </>
                      ) : (
                        t('profile.twoFactor.disableButton')
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}